/**
 * A3 - every message the chain carries, generated from this run.
 *
 * Nothing on this panel is typed in. The settlement name, the arrival clock,
 * the depth, the severity and the expiry all come from the evaluation of the
 * selected run; change the run or the settlement and every tab changes.
 *
 * The CAP tab highlights the XML with a small tokeniser rather than a syntax
 * library: the document is machine-generated, so its shape is known - tags,
 * attributes, text - and three colours are enough to read it.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { download, postJson } from './shared'
import type { LocalisedText, MessageSet, SettlementRow } from './shared'
import { C } from '../../design/tokens'

interface Props {
  runId: string
  settlement: SettlementRow
  rules: Record<string, number> | null
}

const TABS = [
  { key: 'cap', label: 'CAP v1.2 XML' },
  { key: 'sms', label: 'SMS' },
  { key: 'cell', label: 'Cell broadcast' },
  { key: 'ivr', label: 'IVR voice script' },
  { key: 'siren', label: 'Siren pattern' },
  { key: 'email', label: 'Institutional email' },
  { key: 'crawl', label: 'TV and radio crawl' },
] as const
type TabKey = (typeof TABS)[number]['key']

/** Tag / attribute / text, in the three colours the design system has. */
function CapXml({ xml }: { xml: string }) {
  const parts = useMemo(() => {
    const out: { text: string; kind: 'tag' | 'attr' | 'text' | 'decl' }[] = []
    const pattern = /(<\?[^?]*\?>)|(<\/?[A-Za-z0-9_:.-]+)|([A-Za-z0-9_:.-]+=)("[^"]*")|(>)/g
    let last = 0
    let match: RegExpExecArray | null
    while ((match = pattern.exec(xml)) !== null) {
      if (match.index > last) out.push({ text: xml.slice(last, match.index), kind: 'text' })
      if (match[1]) out.push({ text: match[1], kind: 'decl' })
      else if (match[2]) out.push({ text: match[2], kind: 'tag' })
      else if (match[3]) {
        out.push({ text: match[3], kind: 'attr' })
        out.push({ text: match[4], kind: 'text' })
      } else if (match[5]) out.push({ text: match[5], kind: 'tag' })
      last = pattern.lastIndex
    }
    if (last < xml.length) out.push({ text: xml.slice(last), kind: 'text' })
    return out
  }, [xml])

  const colour = {
    tag: 'var(--ink-accent)',
    attr: C.sevSevere,
    decl: 'var(--ink-text-faint)',
    text: 'var(--ink-text)',
  }
  return (
    <pre
      className="font-mono"
      style={{
        fontSize: 'var(--fs-label)',
        lineHeight: 1.6,
        margin: 0,
        maxHeight: 420,
        overflow: 'auto',
        whiteSpace: 'pre',
      }}
    >
      {parts.map((part, index) => (
        <span key={index} style={{ color: colour[part.kind] }}>
          {part.text}
        </span>
      ))}
    </pre>
  )
}

function LanguageBlock({ items, limitLabel }: { items: LocalisedText[]; limitLabel: string }) {
  const labels: Record<string, string> = { en: 'English', hi: 'हिन्दी', mr: 'मराठी' }
  return (
    <div className="grid gap-3">
      {items.map((item) => (
        <div key={item.language}>
          <div className="mb-1 flex items-baseline gap-3">
            <span className="section-label">{labels[item.language] ?? item.language}</span>
            <span
              className="font-mono"
              style={{
                fontSize: 'var(--fs-label)',
                color: item.within_limit === false ? C.sevExtreme : 'var(--ink-text-faint)',
              }}
            >
              {item.characters} / {item.limit} {limitLabel}
            </span>
          </div>
          <div
            style={{
              fontSize: 'var(--fs-cell)',
              lineHeight: 1.55,
              color: 'var(--ink-text)',
              background: 'var(--ink-bg)',
              border: '1px solid var(--ink-border)',
              borderRadius: 'var(--r-control)',
              padding: '8px 10px',
            }}
          >
            {item.text}
          </div>
        </div>
      ))}
    </div>
  )
}

export function MessageTemplates({ runId, settlement, rules }: Props) {
  const [tab, setTab] = useState<TabKey>('cap')
  const messages = useQuery<MessageSet>({
    queryKey: ['alerts-messages', runId, settlement.settlement_id, rules],
    queryFn: () =>
      postJson<MessageSet>(
        `/alerts/runs/${runId}/messages/${settlement.settlement_id}`,
        rules ? { rules } : {},
      ),
  })

  const data = messages.data
  return (
    <section className="panel" style={{ padding: 0 }} data-testid="message-templates">
      <div className="panel-header flex-wrap items-baseline gap-x-4">
        <span className="panel-title">Messages · {settlement.name}</span>
        {data && (
          <span
            className="font-mono"
            style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
          >
            built from run {data.generated_from.run_id}
          </span>
        )}
      </div>
      <div
        className="flex flex-wrap gap-1 px-4 pt-3"
        style={{ borderBottom: '1px solid var(--ink-border)' }}
      >
        {TABS.filter((item) => item.key === 'cap' || !data || (data as unknown as Record<string, unknown>)[item.key === 'cell' ? 'cell_broadcast' : item.key] !== undefined).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            style={{
              fontSize: 'var(--fs-cell)',
              padding: '6px 12px',
              minHeight: 32,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: tab === item.key ? 'var(--ink-text)' : 'var(--ink-text-dim)',
              borderBottom:
                tab === item.key ? '2px solid var(--ink-accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="px-4 pb-4 pt-3">
        {!data ? (
          <div className="skeleton" style={{ height: 240 }} />
        ) : tab === 'cap' ? (
          <>
            <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                status Exercise · msgType Alert · scope Public ·{' '}
                {String((data.cap.card as Record<string, unknown>).polygon_vertices)} polygon
                vertices from the run&rsquo;s own extent
              </span>
              <button
                type="button"
                className="ctl ml-auto"
                onClick={() =>
                  download(
                    `cap_${runId}_${settlement.name}.xml`,
                    'application/xml',
                    data.cap.xml,
                  )
                }
              >
                Download XML
              </button>
            </div>
            <CapXml xml={data.cap.xml} />
          </>
        ) : tab === 'sms' ? (
          <LanguageBlock items={data.sms} limitLabel="GSM characters" />
        ) : tab === 'cell' ? (
          <>
            <div className="section-label mb-2">
              One cell-broadcast page, 3GPP TS 23.041, area-addressed
            </div>
            <LanguageBlock items={data.cell_broadcast} limitLabel="characters" />
          </>
        ) : tab === 'crawl' ? (
          <div className="grid gap-3">
            {data.crawl.map((item) => (
              <div key={item.language}>
                <div className="section-label mb-1">{item.language.toUpperCase()}</div>
                <div
                  style={{
                    fontSize: 'var(--fs-cell)',
                    letterSpacing: '0.02em',
                    color: 'var(--ink-text)',
                    background: 'var(--ink-bg)',
                    border: '1px solid var(--ink-border)',
                    borderRadius: 'var(--r-control)',
                    padding: '8px 10px',
                  }}
                >
                  {item.text}
                </div>
              </div>
            ))}
          </div>
        ) : tab === 'ivr' ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {data.ivr.map((script) => (
              <div key={script.language}>
                <div className="mb-1 flex items-baseline gap-2">
                  <span className="section-label">{script.language.toUpperCase()}</span>
                  <span
                    className="font-mono"
                    style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
                  >
                    ≈{script.spoken_seconds} s spoken
                  </span>
                </div>
                <table className="data-table w-full">
                  <tbody>
                    {script.steps.map((step) => (
                      <tr key={step.cue}>
                        <td
                          style={{
                            width: 78,
                            verticalAlign: 'top',
                            color: 'var(--ink-text-faint)',
                            fontSize: 'var(--fs-label)',
                          }}
                        >
                          {step.cue}
                        </td>
                        <td style={{ lineHeight: 1.5 }}>{step.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : tab === 'siren' ? (
          <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
            <table className="data-table w-full">
              <tbody>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>tone</td>
                  <td>{data.siren.tone}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>on / off</td>
                  <td className="num">
                    {data.siren.on_seconds} s / {data.siren.off_seconds} s
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>repeats</td>
                  <td className="num">{data.siren.repeats}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>total</td>
                  <td className="num">{data.siren.total_seconds} s</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>meaning</td>
                  <td>{data.siren.meaning}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-text-dim)' }}>all clear</td>
                  <td>{data.siren.all_clear}</td>
                </tr>
              </tbody>
            </table>
            <div>
              <div className="section-label mb-2">Pattern</div>
              <svg viewBox={`0 0 ${data.siren.total_seconds} 60`} width="100%" height={60}>
                {data.siren.timeline.map((cycle) => (
                  <rect
                    key={cycle.index}
                    x={cycle.start_s}
                    y={10}
                    width={cycle.on_s}
                    height={40}
                    fill={C.sevExtreme}
                    fillOpacity={0.75}
                  />
                ))}
                <line
                  x1={0}
                  y1={50}
                  x2={data.siren.total_seconds}
                  y2={50}
                  stroke="var(--ink-border)"
                />
              </svg>
              <div
                className="font-mono"
                style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
              >
                0 s → {data.siren.total_seconds} s · {data.siren.sounded_at}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-baseline gap-3">
              <span className="section-label">Subject</span>
              <span style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-text)' }}>
                {data.email.subject}
              </span>
            </div>
            <pre
              style={{
                fontSize: 'var(--fs-cell)',
                lineHeight: 1.6,
                margin: 0,
                whiteSpace: 'pre-wrap',
                color: 'var(--ink-text)',
                background: 'var(--ink-bg)',
                border: '1px solid var(--ink-border)',
                borderRadius: 'var(--r-control)',
                padding: '10px 12px',
              }}
            >
              {data.email.body}
            </pre>
          </>
        )}
      </div>
    </section>
  )
}
